import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [userCount, setUserCount] = useState({});
  const [attendanceData, setAttendanceData] = useState({
    present: 0,
    absent: 0,
    late: 0,
  });
  const [meetingCount, setMeetingCount] = useState({});

  useEffect(() => {
    document.body.id = "dashboard-001";

    const fetchUserCount = async () => {
      try {
        const response = await api.get("/get/user/count");
        if (response.data.success && response.data.status_code === 200) {
          setUserCount(response.data.data);
        } else {
          console.error("Failed to fetch user count:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching user count:", error);
      }
    };

    const fetchAttendance = async () => {
      try {
        const response = await api.get("/attendance/graph");
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          const summary = {
            present: data.present || 0,
            absent: data.absent || 0,
            late: data.late || 0,
          };

          setAttendanceData(summary);
        } else {
          console.error("Failed to fetch attendance:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };


    const fetchMeetingCount = async () => {
      try {
        const response = await api.get("/meeting/count");
        if (response.data.success && response.data.status_code === 200) {
          setMeetingCount(response.data.data);
        } else {
          console.error("Failed to fetch meeting count:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching meeting count:", error);
      }
    };

    fetchUserCount();
    fetchAttendance();
    fetchMeetingCount();
  }, []);

  const dynamicLabels = Object.keys(userCount).filter(
    (key) => key !== "all_users" && key !== "inactive_users"
  );

  const dynamicData = dynamicLabels.map((key) => userCount[key]);

  const dynamicColors = [
    "#4CAF50",
    "#FF9800",
    "#9C27B0",
    "#03A9F4",
    "#E91E63",
    "#8BC34A",
    "#CDDC39",
    "#00BCD4",
  ];

  const employeeChartData = {
    labels: dynamicLabels.map((label) =>
      label.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    ),
    datasets: [
      {
        label: "Employees",
        data: dynamicData,
        backgroundColor: dynamicColors.slice(0, dynamicLabels.length),
        borderColor: dynamicColors
          .slice(0, dynamicLabels.length)
          .map((color) => color.replace("4", "3")),
        borderWidth: 1,
        borderRadius: 5,
        barThickness: 30,
      },
    ],
  };

  const employeeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 14 } },
        grid: { borderDash: [4, 4], color: "#e0e0e0" },
      },
      x: {
        ticks: { font: { size: 14 } },
        grid: { display: false },
      },
    },
  };

  const attendanceChartData = {
    labels: [
      `Present (${attendanceData.present})`,
      `Absent (${attendanceData.absent})`,
      `Late (${attendanceData.late})`,
    ],
    datasets: [
      {
        label: "Attendance",
        data: [
          attendanceData.present,
          attendanceData.absent,
          attendanceData.late,
        ],
        backgroundColor: ["#4CAF50", "#F44336", "#FF9800"],
        borderColor: ["#388E3C", "#D32F2F", "#F57C00"],
        borderWidth: 1,
      },
    ],
  };

  const attendanceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 20,
          padding: 15,
          font: { size: 14 },
        },
      },
    },
  };

  const meetingLabels = Object.keys(meetingCount).map((key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );

  const meetingValues = Object.values(meetingCount);

  const meetingChartData = {
    labels: meetingLabels,
    datasets: [
      {
        label: "Meetings",
        data: meetingValues,
        backgroundColor: ["#3F51B5", "#00BCD4"],
        borderColor: ["#303F9F", "#0097A7"],
        borderWidth: 1,
        borderRadius: 5,
        barThickness: 30,
      },
    ],
  };

  const meetingChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: { size: 14 },
        },
        grid: {
          borderDash: [4, 4],
          color: "#e0e0e0",
        },
      },
      x: {
        ticks: {
          font: { size: 14 },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <section className="dashbord-section" style={{ padding: "20px" }}>
      <div className="row-wrapper">
        <div className="card">
          <h3 style={{ marginBottom: "20px" }}>Total Employees</h3>
          <div style={{ height: "250px" }}>
            <Bar data={employeeChartData} options={employeeChartOptions} />
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: "20px" }}>Attendance Summary</h3>
          <div style={{ height: "250px" }}>
            <Doughnut
              data={attendanceChartData}
              options={attendanceChartOptions}
            />
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: "20px" }}>Meeting Summary</h3>
          <div style={{ height: "250px" }}>
            <Bar data={meetingChartData} options={meetingChartOptions} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
